import { useCallback, useEffect, useRef, useState } from 'react';
import type { HouseAd } from '../ads/houseAds';
import { useAds } from './useAds';

// Free tier: after 60 minutes of *visible* viewing time, show one house ad for 30 s,
// then resume. Viewing time persists across reloads so refreshing doesn't skip the ad.
// Licensed users never mount the timer (see App).

export const AD_INTERVAL_MS = 60 * 60_000;
export const AD_DURATION_MS = 30_000;
const STORAGE_KEY = 'kcl-canvas-ad-clock';
const TICK_MS = 5000;

function readElapsed(): number {
  try {
    return Number(localStorage.getItem(STORAGE_KEY) ?? 0) || 0;
  } catch {
    return 0;
  }
}

function writeElapsed(ms: number): void {
  try {
    localStorage.setItem(STORAGE_KEY, String(ms));
  } catch {
    // storage blocked – the in-memory counter still works
  }
}

export function useAdManager(enabled: boolean) {
  const [ad, setAd] = useState<HouseAd | null>(null);
  const elapsed = useRef(readElapsed());
  const ads = useAds(enabled);
  const adsRef = useRef(ads);
  adsRef.current = ads;
  const adIndex = useRef(Math.floor(Math.random() * 1000));

  const dismiss = useCallback(() => setAd(null), []);

  useEffect(() => {
    if (!enabled) {
      setAd(null);
      return;
    }
    const timer = window.setInterval(() => {
      if (document.visibilityState !== 'visible' || ad) return;
      elapsed.current += TICK_MS;
      if (elapsed.current >= AD_INTERVAL_MS) {
        elapsed.current = 0;
        const list = adsRef.current;
        adIndex.current = (adIndex.current + 1) % list.length;
        setAd(list[adIndex.current] ?? null);
      }
      writeElapsed(elapsed.current);
    }, TICK_MS);
    return () => window.clearInterval(timer);
  }, [enabled, ad]);

  useEffect(() => {
    if (!ad) return;
    const t = window.setTimeout(dismiss, AD_DURATION_MS);
    return () => window.clearTimeout(t);
  }, [ad, dismiss]);

  return { ad, dismiss };
}
